import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon, Label, Popup } from 'semantic-ui-react';
import { config } from '../../constants/config';
import { fetchUserSignedUrlPathV2 } from '../../utils/fetchSignedUrlByFolderV2';
import { getVariationStatus } from './variationStatusConfig';
import { selectCloneBySampleId } from '../../slices/voiceCloningSlice';
import { getCloneStatus } from '../voiceCloning/cloneStatusConfig';
import '../../styles/opportunities.css';
import '../../styles/chevronToggle.css';

/**
 * VariationReviewRow — renders one variation with playback + accept/reject controls
 * Used inside ManageOpportunities table for Book Creator review workflow.
 *
 * Props:
 *   variation       — the variation object (carries emotion/name/sampleStatus/voiceSnippetUrl/sampleUuid)
 *   onApprove       — callback fired after a successful approve
 *   onReject        — callback fired after a successful reject
 *   showStatusText  — when true, renders "  - <Status>" inline on the
 *                     colored label so the status word is visible without
 *                     hovering. Default OFF (keeps the label compact);
 *                     the status is still available via the hover/focus
 *                     tooltip, aria-label, and the column-header legend.
 */
const VariationReviewRow = ({
  variation,
  onApprove,
  onReject,
  showStatusText = false,
  // Recording-plan state, supplied by ManageOpportunities. All optional: rendered elsewhere
  // (the marketplace, opportunity details) this component behaves exactly as it always did.
  inBudget,
  isExcluded = false,
  seconds,
  // Whether occurrence counts exist at all for this character. Without it a count of 0 is
  // ambiguous — "never spoken" and "casting not derived yet" look identical, and only one of
  // them is a fact.
  ranked = false,
  // Print the passage under the included rows. Off by default so the marketplace and the
  // opportunity-detail views, which render this same row, are unchanged.
  showText = false,
  onToggleExclude
}) => {

  /**
   * Whether the passage is shown. Collapsed by default — the row is a list item first.
   *
   * ⭐ PER-ROW, DELIBERATELY, and it supersedes the deferred "show/hide all text" toggle. That one
   * carried a warning worth keeping: 53 of one character's 100 variations hold the IDENTICAL
   * passage, so an expand-all prints the same paragraph 53 times. Expanding one row at a time is
   * the granularity that makes the text useful instead of a wall.
   *
   * ⚠️ Rows are keyed by `variation.uuid` in ManageOpportunities, so an open row stays open across
   * a filter refetch. That matters now that a filter takes long enough to notice — silently
   * collapsing what the operator had opened would read as the page losing its place.
   */
  const [textOpen, setTextOpen] = useState(false);

  // ⚠️ Must be unique per row. Many of these render in one list, and a duplicated id would make
  // every chevron's aria-controls point at the first row's passage — the control would announce
  // one thing and operate another.
  const textBlockId = `var-text-${variation?.uuid || variation?.variationName || 'row'}`;

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'approve' | 'reject' | null
  const audioRef = useRef(null);
  const [signedUrl, setSignedUrl] = useState(null);

  // Both Popups (variation status + clone-status corner dot) are
  // controlled so we can suppress the variation popup whenever the
  // dot popup is active. Without this they would stack on top of
  // each other when the user hovers/focuses the dot, since the dot
  // sits inside the variation Label's trigger span.
  const [variationPopupOpen, setVariationPopupOpen] = useState(false);
  const [clonePopupOpen, setClonePopupOpen] = useState(false);

  const status = variation.sampleStatus || 'waiting';
  const statusConfig = getVariationStatus(status);
  // Emotion alone is not a unique label. `intensity` separates the derived variation (0) from the
  // standard category seed ('standard') where an emotion is both — see the note on the server
  // projection. A 0 intensity is the ordinary case and says nothing worth printing.
  const intensityLabel = ['', '0', 'null', 'undefined'].includes(String(variation.intensity ?? ''))
    ? '' : String(variation.intensity);
  const variationName = [variation.emotion || variation.name || 'Unnamed', intensityLabel]
    .filter(Boolean).join(' · ');
  const hasAudio = status !== 'waiting';
  const isSubmitted = status === 'submitted';
  const isReviewed = status === 'approved' || status === 'rejected';

  // Clone-pipeline status for this sample — only meaningful after approval.
  // The row is populated once fetchPipeline() has run; before that (or for
  // non-approved variations) `cloneRow` will be null and no badge renders.
  const cloneRow = useSelector(selectCloneBySampleId(variation.sampleUuid));
  const cloneStatus = cloneRow?.cloneStatus || null;

  // Optional add-on: only render the corner-dot indicator once the clone
  // pipeline has actually started (i.e. there's a non-virtual status).
  // The 'none' key is a client-side virtual placeholder used by the
  // VoiceCloningPipeline page; in this row we don't want a dot until
  // the BC has actually seeded/launched the pipeline, so unstarted
  // variations stay visually clean.
  const cloneStatusConfig = cloneStatus ? getCloneStatus(cloneStatus) : null;
  const showCloneDot = !!cloneStatusConfig && !cloneStatusConfig.virtual;


  /**
   * Fetch a signed URL and play the audio
   */
  const handlePlayback = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    if (!variation.voiceSnippetUrl) return;

    try {
      setAudioLoading(true);

      // Get signed URL using the proven utility (caching, auto-refresh, error fallback)
      let url = signedUrl;
      if (!url) {
        // Extract userId from the S3 URL path (format: .../aai-users/{userId}/...)
        const userIdMatch = variation.voiceSnippetUrl.match(/aai-users\/([^/]+)/);
        const userId = userIdMatch ? userIdMatch[1] : null;

        if (userId) {
          url = await fetchUserSignedUrlPathV2(userId, variation.voiceSnippetUrl);
          if (url) setSignedUrl(url);
        } else {
          console.warn('[VariationReviewRow] Could not extract userId from path:', variation.voiceSnippetUrl);
        }
      }

      if (url) {
        if (!audioRef.current) {
          audioRef.current = new Audio(url);
          audioRef.current.onended = () => setIsPlaying(false);
        } else {
          audioRef.current.src = url;
        }
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Playback error:', err);
    } finally {
      setAudioLoading(false);
    }
  };

  /**
   * Handle approve action
   */
  const handleApprove = async () => {
    if (!variation.sampleUuid || actionLoading) return;
    setActionLoading('approve');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        config.APPROVE_SPEECH_SAMPLE.replace(':sampleId', variation.sampleUuid),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (onApprove) onApprove(variation, result);
      }
    } catch (err) {
      console.error('Approve error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle reject action
   */
  const handleReject = async () => {
    if (!variation.sampleUuid || actionLoading) return;
    setActionLoading('reject');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        config.REJECT_SPEECH_SAMPLE.replace(':sampleId', variation.sampleUuid),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (onReject) onReject(variation, result);
      }
    } catch (err) {
      console.error('Reject error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // NOT RECORDED reads grey, and it is the resting state of almost every row: a character has a
  // variation for every emotion it can perform, and only a handful have ever been performed. The
  // status badge alone made "waiting" and "recorded" nearly identical at a glance in a list of
  // sixty rows, so the whole row now carries the distinction, not just its badge.
  const notRecorded = !hasAudio;

  // How the passage got here, said plainly. The distinction the badge is carrying: a category
  // passage was written for an EMOTION and is shared by every character in every book, while a
  // character passage was written from this character's own lines.
  const PROMPT_SOURCE_STYLES = {
    character: { text: 'In this voice', bar: '#21ba45', label: { backgroundColor: '#e8f6ec', color: '#1a7a33' } },
    emotion: { text: 'Emotion text', bar: '#a5a5a5', label: { backgroundColor: '#f1f1f1', color: '#666' } },
    category: { text: 'Category text — shared by every character', bar: '#cdcdcd', label: { backgroundColor: '#f6f6f6', color: '#8a8a8a' } },
    'category-seed': { text: 'Category seed - the standard set every character gets', bar: '#cdcdcd', label: { backgroundColor: '#f6f6f6', color: '#8a8a8a' } },
    'neutral-seed': { text: 'Neutral seed - the baseline passage', bar: '#cdcdcd', label: { backgroundColor: '#f6f6f6', color: '#8a8a8a' } },
    // ⛔ THE ONE THAT SHOULD ALARM. Set when the registry lookup failed and the recording prompt
    // fell back to ACTUAL BOOK TEXT — which makes ElevenLabs reproduce the recorded take instead
    // of learning a voice. Zero exist today; it must never read as ordinary if one appears.
    'legacy-booktext': { text: 'Book text - should not be recorded', bar: '#db2828', label: { backgroundColor: '#fff0f0', color: '#b32424' } }
  };
  // ⭐ FALL BACK TO THE SEEDER FLAGS BEFORE GIVING UP. The two seeders record what they made in
  // `isStandardSeed` / `isNeutralSeed` rather than in `promptSource`, so reading only the latter
  // called 37,292 snippets unrecorded when 37,202 of them said exactly what they were. "I do not
  // know" is only honest when nothing knows.
  const promptSourceStyle =
    PROMPT_SOURCE_STYLES[variation?.promptSource]
    || (variation?.isStandardSeed ? PROMPT_SOURCE_STYLES['category-seed'] : null)
    || (variation?.isNeutralSeed ? PROMPT_SOURCE_STYLES['neutral-seed'] : null)
    || { text: 'Source unrecorded', bar: '#e0e0e0', label: { backgroundColor: '#fafafa', color: '#999' } };

  // Out of the clone's sample set — either the operator excluded it, or it lost the duration
  // budget. Dimmed rather than hidden: which emotions did NOT make the cut is exactly what a
  // voice actor needs to see before recording, and a hidden row teaches nothing.
  const outOfPlan = isExcluded || inBudget === false;

  return (
    <div
      className='variation-review-cv-container'
      style={{
        backgroundColor: notRecorded ? '#f4f4f5' : undefined,
        opacity: outOfPlan ? 0.45 : 1,
        borderLeft: isExcluded ? '3px solid #db2828'
          : (inBudget === true ? '3px solid #21ba45' : '3px solid transparent'),
        paddingLeft: inBudget === undefined && !isExcluded ? undefined : '4px',
        borderRadius: '3px',
        // These rows are cells of a THREE-COLUMN grid (.manage-op-var-row-container), so each is
        // about a third of the column wide. A flex child defaults to min-width:auto and refuses to
        // shrink below its content, so an extra control does not compress the row — it pushes the
        // whole thing past its cell and into the neighbour. Allow the shrink, and wrap rather than
        // overflow when even that is not enough.
        minWidth: 0,
        flexWrap: 'wrap'
      }}
    >
      {/* Status badge — wrapped in a Popup so the meaning of the color/icon
          is discoverable on hover AND keyboard focus (accessibility).
          When `showStatusText` is true, the label also renders "  - <Status>"
          inline; by default this is off (kept compact), but the status is
          still available via tooltip, aria-label, and the column legend. */}
      <Popup
        on={['hover', 'focus']}
        position="top left"
        // Controlled open: hide the variation popup whenever the
        // corner-dot clone popup is active so the two tooltips don't
        // stack on top of each other (the dot lives inside this trigger
        // span, so its hover/focus would otherwise also fire this one).
        open={variationPopupOpen && !clonePopupOpen}
        onOpen={() => setVariationPopupOpen(true)}
        onClose={() => setVariationPopupOpen(false)}
        trigger={
          <span
            tabIndex={0}
            aria-label={`${variationName} — ${statusConfig.label}: ${statusConfig.description}`}
            style={{ display: 'inline-block', position: 'relative' }}
          >
            <Label
              size="tiny"
              color={statusConfig.color}
              style={{
                minWidth: '90px',
                // inline-flex aligns the icon + text on a shared center axis.
                // Left-justify the contents (icon first, then text).
                // lineHeight:1 on the text spans removes the extra descender
                // gap that was making the word look bottom-biased relative
                // to the icon's optical center.
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                lineHeight: 1,
                textAlign: 'left'
              }}
            >
              <Icon
                name={statusConfig.icon}
                style={{ margin: 0, lineHeight: 1, verticalAlign: 'middle' }}
              />
              <span
                style={{
                  lineHeight: 1,
                  // Nudge the text up ~1px so its x-height sits on the
                  // icon's optical center rather than its baseline.
                  position: 'relative',
                  top: '-1px',
                  verticalAlign: 'middle'
                }}
              >
                {variationName}
              </span>
              {showStatusText && (
                <span
                  style={{
                    opacity: 0.85,
                    fontWeight: 'normal',
                    lineHeight: 1,
                    position: 'relative',
                    top: '-1px',
                    verticalAlign: 'middle'
                  }}
                >
                  {` - ${statusConfig.label}`}
                </span>
              )}
            </Label>

            {/* Voice-clone pipeline status — corner-dot overlay.
                Replaces the previous inline CloneStatusBadge so the
                3-column grid layout has room for the play / accept /
                reject buttons without overflowing. The dot's color
                mirrors the cloneStatusConfig.color, and a Popup
                (hover + focus) reveals the same label + description
                the inline badge previously showed. */}
            {showCloneDot && (
              <Popup
                on={['hover', 'focus']}
                position="top right"
                open={clonePopupOpen}
                onOpen={() => setClonePopupOpen(true)}
                onClose={() => setClonePopupOpen(false)}
                offset={[12, 0]}
                trigger={
                  <span
                    tabIndex={0}
                    aria-label={`Clone status: ${cloneStatusConfig.label} — ${cloneStatusConfig.description}`}
                    className={`ui ${cloneStatusConfig.color} circular label`}
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      width: '14px',
                      height: '14px',
                      minWidth: '14px',
                      minHeight: '14px',
                      padding: 0,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      fontSize: '8px',
                      cursor: 'help',
                      zIndex: 1
                    }}
                  >
                    <Icon
                      name={cloneStatusConfig.icon}
                      style={{
                        margin: 0,
                        fontSize: '8px',
                        lineHeight: 1,
                        height: '8px',
                        width: '8px'
                      }}
                    />
                  </span>
                }
              >
                <Popup.Header>
                  <Icon
                    name={cloneStatusConfig.icon}
                    color={cloneStatusConfig.color}
                  />
                  Clone: {cloneStatusConfig.label}
                </Popup.Header>
                <Popup.Content>{cloneStatusConfig.description}</Popup.Content>
              </Popup>
            )}

          </span>
        }
      >
        <Popup.Header>
          <Icon name={statusConfig.icon} color={statusConfig.color} />
          {statusConfig.label}
        </Popup.Header>
        <Popup.Content>{statusConfig.description}</Popup.Content>
      </Popup>

      {/* Playback button */}
      <Popup
        content={hasAudio ? (isPlaying ? 'Pause' : 'Play') : 'No recording yet'}
        trigger={
          <Button
            icon
            size="mini"
            color={isPlaying ? 'orange' : undefined}
            disabled={!hasAudio}
            loading={audioLoading}
            onClick={handlePlayback}
            style={{ padding: '4px 6px' }}
          >
            <Icon name={isPlaying ? 'pause' : 'play'} />
          </Button>
        }
      />

      {/* Accept button */}
      <Popup
        content={isReviewed ? `Already ${status}` : (isSubmitted ? 'Accept this recording' : 'Must be submitted first')}
        trigger={
          <Button
            icon
            size="mini"
            color={status === 'approved' ? 'purple' : 'green'}
            disabled={!isSubmitted || !!actionLoading}
            loading={actionLoading === 'approve'}
            onClick={handleApprove}
            style={{ padding: '4px 6px' }}
          >
            <Icon name="check" />
          </Button>
        }
      />

      {/* Reject button */}
      <Popup
        content={isReviewed ? `Already ${status}` : (isSubmitted ? 'Reject this recording' : 'Must be submitted first')}
        trigger={
          <Button
            icon
            size="mini"
            color={status === 'rejected' ? 'red' : undefined}
            disabled={!isSubmitted || !!actionLoading}
            loading={actionLoading === 'reject'}
            onClick={handleReject}
            style={{ padding: '4px 6px' }}
          >
            <Icon name="times" />
          </Button>
        }
      />

      {/* Include / exclude this one variation from the voice clone.
        *
        * The budget cut ranks by how often the emotion is spoken, which is a sound default and a
        * poor verdict — someone who has HEARD a take may know it is a bad one, or that a rarely
        * spoken emotion is the dramatic peak of the book. The ranking cannot know either, so it
        * can be overruled per row, and the header total moves when it is. */}
      {onToggleExclude && (
        <Popup
          content={isExcluded
            ? 'Excluded from the voice clone — click to put it back'
            : (inBudget === false
              ? 'Past the clone budget — click to exclude it explicitly'
              : 'Included in the voice clone — click to keep it out')}
          trigger={
            <Button
              icon
              size="mini"
              basic={!isExcluded}
              color={isExcluded ? 'red' : undefined}
              onClick={() => onToggleExclude(variation, !isExcluded)}
              style={{ padding: '4px 6px' }}
            >
              <Icon name={isExcluded ? 'ban' : 'check circle outline'} />
            </Button>
          }
        />
      )}


      {/* How long this passage costs the recording session — right edge of the button line.
        *
        * It sat inline between the name and the play button, where its width varies with the
        * value ("~9.5s" against "~120.0s") and every row of a sixty-row list put the playback
        * button in a slightly different place. Controls that move under the cursor are worse than
        * controls that are further away.
        *
        * marginLeft: auto consumes the free space to its left instead of taking a line of its
        * own, so it sits at the right edge alongside the play / approve / reject / include
        * controls and its width changes push into emptiness rather than shoving the buttons. It
        * must still be the LAST child for that to work. The same trick the clone-status badge
        * used in this row before it moved to the corner dot.
        *
        * MEASURED vs ESTIMATED is the distinction worth carrying, and the tilde carries it. Until
        * a passage is recorded AND normalized there is no real duration, so the figure is a mean
        * passage at 150 wpm — a slower dramatic read runs longer, which means the estimate errs
        * SHORT. Someone budgeting a session off these numbers should know which ones are guesses,
        * because a column of confident-looking seconds that are all inferred is worse than no
        * column at all. */}
      {seconds > 0 && (
        <Popup
          size="mini"
          content={Number(variation?.durationSeconds) > 0
            ? (ranked
              ? `${Number(variation?.occurrenceCount) || 0} lines spoken in this emotion. Measured length of the recording.`
              : 'Measured length of the recording')
            : (Number(variation?.wordCount) > 0
              ? `${ranked ? `${Number(variation?.occurrenceCount) || 0} lines spoken in this emotion. ` : ''}` +
                `Estimated from this passage: ${variation.wordCount} words at 150 wpm. ` +
                'A dramatic read runs slower, so the real take will be longer.'
              : 'Estimated — no recording and no passage text, so this is a corpus average')}
          trigger={
            <span
              style={{
                marginLeft: 'auto',
                textAlign: 'right',
                paddingRight: '10px',
                fontSize: '0.75em',
                color: Number(variation?.durationSeconds) > 0 ? '#666' : '#aaa',
                fontVariantNumeric: 'tabular-nums',
                cursor: 'default',
                lineHeight: 1.2
              }}
            >
              {ranked && (
                <span style={{ marginRight: '8px' }}>
                  {Number(variation?.occurrenceCount) || 0} ln
                </span>
              )}
              {Number(variation?.durationSeconds) > 0 ? '' : '~'}{Number(seconds).toFixed(1)}s
            </span>
          }
        />
      )}

      {/* Note: clone-pipeline status used to render here as an inline
          CloneStatusBadge with `marginLeft: auto`. It now renders as a
          small corner-dot on the variation Label above so the row fits
          inside the Character / Variations 3-column grid. The same
          label + description are still available via the dot's hover
          / focus Popup. */}

      {/* ─── WHAT THE ACTOR WILL ACTUALLY SAY ───────────────────────────────────────────────
        *
        * ⭐ INCLUDED ROWS ONLY. A character carries a variation for every emotion it can
        * perform — Glendyl has 108 — and printing a passage under each would bury the handful
        * that matter. The included set is the one being decided about, and it is ~9 rows.
        *
        * This existed nowhere before: the Book Creator could commission a rewrite and then had
        * to log in as the voice actor to find out what had been written. A write with no read.
        *
        * The source badge is the point of the block as much as the text is. "Category" means
        * this passage was written for an emotion and is shared by every character in every
        * book; "In voice" means it was written from this character's own lines. They read very
        * differently aloud, and until now nothing on the page said which one you had. */}
      {showText && inBudget === true && variation?.textContent && (
        <div
          style={{
            // The parent is a wrapping flex row, so a 100% basis puts this on its own line
            // beneath the badge and counters rather than squeezing in beside them.
            flex: '1 0 100%',
            marginTop: 6,
            paddingLeft: 8,
            borderLeft: `1px solid ${promptSourceStyle.bar}`,
            fontSize: '0.8em',
            lineHeight: 1.45,
            color: '#555'
          }}
        >
          {/* ── The collapsed row: chevron + badge, on one line ──────────────────────────────
            *
            * ⭐ THE BADGE STAYS VISIBLE WHEN COLLAPSED. It is the point of this block as much as
            * the text is — "Category" means the passage was written for an emotion and is shared
            * by every character in every book, "In voice" means it came from this character's own
            * lines, and they read very differently aloud. Hiding it behind the chevron would
            * conceal the one signal the block was added to provide, while the TEXT is what
            * actually costs vertical space. So the badge is always shown and only the passage
            * folds away.
            *
            * ⚠️ A real <button>, not a clickable div: this row already carries approve/reject and
            * play controls, and a keyboard user needs this one on the same terms. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: textOpen ? 4 : 0 }}>
            <button
              type="button"
              className="aai-chevron-toggle"
              onClick={() => setTextOpen((o) => !o)}
              aria-expanded={textOpen}
              aria-controls={textBlockId}
              title={textOpen ? 'Hide the passage' : 'Show the passage'}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                cursor: 'pointer',
                color: '#777',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1
              }}
            >
              <Icon name={textOpen ? 'angle down' : 'angle right'} style={{ margin: 0 }} />
            </button>
            <Label size="mini" style={{ margin: 0, ...promptSourceStyle.label }}>
              {promptSourceStyle.text}
            </Label>
          </div>

          {/* Everything below folds. `hidden` rather than unmounting, so the browser keeps it
            * findable by in-page search and the toggle costs no re-render of the content. */}
          <div id={textBlockId} hidden={!textOpen}>
          {variation.promptDirective && (
            // ⚠️ LABEL IT, BECAUSE IT IS BOOK TEXT AND THE PASSAGE IS NOT.
            //
            // This is the author's own narration — the cue that told the generator how the line
            // sounded — and the block below is newly written words for the actor to perform.
            // Stacked unlabelled they read as one thing, and the first person to see them asked
            // which half was which. Two kinds of text with opposite purposes need saying apart.
            <div style={{ marginBottom: 5 }}>
              {/* ⚠️ ALWAYS THE BOOK, TODAY. `promptDirective` is written in exactly one place —
                * `writeSampleText` — from the narration cue the evidence ranking chose, so it is
                * the author's words by construction. The generator does accept a caller-supplied
                * `directive`, and nothing passes one; the day something does, this label stops
                * being true and has to say which kind it is showing. */}
              <div style={{ fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#a0a0a0' }}>
                Direction from the book
              </div>
              <div style={{ fontStyle: 'italic', color: '#7b7b7b' }}>
                {variation.promptDirective}
              </div>
            </div>
          )}
          <div style={{ fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#a0a0a0' }}>
            Text to voice
          </div>
          <div>{variation.textContent}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariationReviewRow;
